import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Button, Modal } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Check, ExternalLink, ExternalLinkIcon, MousePointer2, RefreshCcw, Zap } from 'lucide-react';
import { EXTENSION_URL, checkExtensionAvailability } from 'services/chromeExtension';

export const RefreshContactModal = ({ onCloseModal, linkedinUrl, trackRefreshEvent }) => {
  const [isExtensionAvailable, setExtensionAvailability] = useState(false);
  useEffect(() => {
    checkExtensionAvailability().then((value) => {
      setExtensionAvailability(value);
    });
  }, []);
  const [checked, setChecked] = useState([]);

  const installOptions = [
    {
      icon: Zap,
      text: 'Automatic on-going sync of your LinkedIn connections',
    },
    {
      icon: MousePointer2,
      text: 'Add and manage Contacts directly from LinkedIn',
    },
    {
      icon: RefreshCcw,
      text: 'Refresh any profile information at any time',
    },
  ];

  const refreshOptions = [
    {
      id: 1,
      text: (
        <Flex
          onClick={() => {
            if (!checked.includes(1)) {
              setChecked([...checked, 1]);
              trackRefreshEvent();
            }
          }}
          sx={{ alignItems: 'center', gap: '8px' }}
        >
          <Box target="__blank" as="a" href={linkedinUrl}>
            Visit contact’s profile
          </Box>
          <Box as={ExternalLinkIcon} size={16} />
        </Flex>
      ),
    },
    {
      id: 2,
      text: (
        <Flex
          onClick={() => {
            if (!checked.includes(2)) {
              setChecked([...checked, 2]);
              trackRefreshEvent();
            }
          }}
          sx={{ alignItems: 'center', gap: '8px' }}
        >
          <Box target="__blank" as="a" href={`${linkedinUrl}/details/experience`}>
            Visit contact’s work experience{' '}
          </Box>
          <Box as={ExternalLinkIcon} size={16} />
        </Flex>
      ),
    },
    {
      id: 3,
      text: (
        <Box>
          <Box>
            {checked.length > 1
              ? 'Processing new information (including AI Matches, and Hires)...'
              : 'New information (including AI Matches, and Hires) is available'}
          </Box>
          <Box>It can take a few minutes for the new information to be ready in Getro.</Box>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ cursor: 'default' }} data-testid="refresh-contact-modal" onClick={(e) => e.stopPropagation()}>
      {createPortal(
        <Modal title="We couldn’t enrich this contact’s profile" onCancel={onCloseModal}>
          <Flex sx={{ flexDirection: 'column', gap: '24px', fontSize: '14px' }}>
            <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
              <Text>
                When you add a contact, Getro automatically fills in their info using LinkedIn and other sources.
              </Text>
              <Text>
                We couldn’t complete this profile, likely because the LinkedIn URL is broken or the profile is private.
              </Text>
            </Flex>
            <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
              <Text fontWeight="semibold" color="text.dark">
                Enrich it with our Chrome extension!
              </Text>
              <Box sx={{ flexDirection: 'column', gap: '16px', display: 'flex' }}>
                {!isExtensionAvailable && (
                  <>
                    <Text>Manually enrich contacts and unlock additional features to streamline your workflow:</Text>
                    {installOptions.map(({ icon, text }) => (
                      <Flex key={text} sx={{ gap: '8px', alignItems: 'center' }}>
                        <Box as={icon} sx={{ color: 'purple.300', size: 20 }} />
                        <Box>{text}</Box>
                      </Flex>
                    ))}
                  </>
                )}

                {isExtensionAvailable && (
                  <>
                    <Box>
                      Thanks to our Chrome extension, any time you visit your contacts&apos; LinkedIn profiles, they are
                      automatically updated in Getro.
                    </Box>
                    <Box sx={{ gap: '8px', alignItems: 'center', flexDirection: 'column' }}>
                      <Box>
                        {refreshOptions.map((option, index) => (
                          <Flex sx={{ gap: '8px', alignItems: 'baseline' }}>
                            <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
                              {checked.includes(option.id) ? (
                                <Flex
                                  data-testId={`${option.id}-checked`}
                                  sx={{
                                    backgroundColor: 'purple.400',
                                    borderRadius: '40px',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '24px',
                                    height: '24px',
                                  }}
                                >
                                  <Box as={Check} size="16px" sx={{ color: 'neutral.0' }} />
                                </Flex>
                              ) : (
                                <Flex
                                  sx={{
                                    color: 'text.main',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    width: '24px',
                                    height: '24px',
                                    backgroundColor: 'neutral.100',
                                    borderRadius: '100%',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  {option.id}
                                </Flex>
                              )}
                              {index !== refreshOptions.length - 1 && (
                                <Box
                                  width="2px"
                                  height="24px"
                                  sx={{
                                    width: '2px',
                                    height: '32px',
                                    backgroundColor: 'neutral.100',
                                  }}
                                />
                              )}
                            </Flex>
                            {option.text}
                          </Flex>
                        ))}
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </Flex>
            {!isExtensionAvailable && (
              <Button
                sx={{
                  width: '100%',
                  justifyContent: 'center',
                }}
                as="a"
                type="link"
                data-testid="submit-btn"
                icon={ExternalLink}
                iconPosition="right"
                target="__blank"
                iconGap="8px"
                href={EXTENSION_URL}
              >
                Install extension
              </Button>
            )}
          </Flex>
        </Modal>,
        document.getElementById('portals'),
      )}
    </Box>
  );
};

RefreshContactModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  trackRefreshEvent: PropTypes.func.isRequired,
  linkedinUrl: PropTypes.string.isRequired,
};
